import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { useScheduleBoardModals } from 'af-root/hooks/useScheduleBoardModal';

import type ScheduleBoardWorkOrderViewModel from 'ab-socketModels/viewModels/scheduleBoard/scheduleBoardWorkOrder.viewModel';

import { isAllowed } from '@acceligentllc/shared/utils/auth';

import type { NotificationStatusEnum } from '@acceligentllc/shared/enums/notificationStatus';

import type { RootState } from 'af-reducers';

import PagePermissions from 'ab-enums/pagePermissions.enum';

import * as ScheduleBoardActions from 'af-actions/scheduleBoard';

import CardInfo from './CardInfo';

interface OwnProps {
	dueDate: string;
	isCalculationsView: boolean;
	isDragging?: boolean;
	lastOpenedOrderCode?: string;
	notificationStatus: Nullable<NotificationStatusEnum>;
	projectManagerName: string;
	supervisorName: string;
	workOrder: ScheduleBoardWorkOrderViewModel;
	shouldRoundTimeIntervalsTo1Min: boolean;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const OrderInfo: React.FC<Props> = (props) => {
	const {
		workOrder,
		projectManagerName,
		supervisorName,
		scheduleBoardView,
		setCopiedWorkOrderCode,
		isDragging,
		isCalculationsView,
		notificationStatus,
		notificationsEnabled,
		zoomLevel,
		isAllowedToEditWorkOrders,
		isAllowedToViewProdData,
		lastOpenedOrderCode,
		hasOnlyNonBillableWork,
		shouldRoundTimeIntervalsTo1Min,
	} = props;

	const { workOrderModalId, setWorkOrderModalData } = useScheduleBoardModals();

	const [modalOpenedForCurrentWorkOrder, setModalOpenedForCurrentWorkOrder] = React.useState(false);

	React.useEffect(() => {
		setModalOpenedForCurrentWorkOrder(workOrder?.id === workOrderModalId);
	}, [workOrderModalId, workOrder?.id]);

	// component did mount
	React.useEffect(() => {
		if (!isCalculationsView && lastOpenedOrderCode === workOrder.code) {
			setWorkOrderModalData(workOrder.id);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		if (isCalculationsView) {
			return;
		}

		if (modalOpenedForCurrentWorkOrder && (workOrder.locked || workOrder.isDisabled)) {
			setWorkOrderModalData(null);
		}
	}, [isCalculationsView, modalOpenedForCurrentWorkOrder, setWorkOrderModalData, workOrder.isDisabled, workOrder.locked]); //

	const openWorkOrderModal = React.useCallback(async () => {
		if (isCalculationsView) {
			return;
		}
		// fixed syncing error
		if (workOrder?.id) {
			setWorkOrderModalData(workOrder.id);
		}
	}, [isCalculationsView, setWorkOrderModalData, workOrder.id]);

	const isInternal = workOrder.isInternal;

	let className = 'schedule-board-card-order-info';
	className = isInternal ? `${className} internal-card` : className;

	return (
		<div className={className}>
			<CardInfo
				hasOnlyNonBillableWork={hasOnlyNonBillableWork}
				isAllowedToEditWorkOrders={isAllowedToEditWorkOrders}
				isAllowedToViewProdData={isAllowedToViewProdData}
				isCalculationsView={isCalculationsView}
				isDragging={isDragging}
				notificationsEnabled={notificationsEnabled}
				notificationStatus={notificationStatus}
				openOrderInfoModal={openWorkOrderModal}
				projectManagerName={projectManagerName}
				scheduleBoardView={scheduleBoardView}
				setCopiedWorkOrderCode={setCopiedWorkOrderCode}
				shouldRoundTimeIntervalsTo1Min={shouldRoundTimeIntervalsTo1Min}
				supervisorName={supervisorName}
				workOrder={workOrder}
				zoomLevel={zoomLevel}
			/>
		</div>
	);
};

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
	const { workOrder } = ownProps;
	const { scheduleBoardView, workOrdersWithOnlyNonBillableWork } = state.scheduleBoard;
	const { companyData, userData } = state.user;
	const { company } = state.company;
	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const isAllowedToEditWorkOrders: boolean = isAllowed(
		PagePermissions.COMPANY.WORK_ORDERS.MANAGE,
		companyData.permissions,
		companyData.isCompanyAdmin,
		userData.role
	);

	const isAllowedToViewProdData: boolean = isAllowed(
		PagePermissions.COMPANY.PROD_DATA.VIEW,
		companyData.permissions,
		companyData.isCompanyAdmin,
		userData.role
	);

	return {
		scheduleBoardView,
		notificationsEnabled: company?.notification?.isEnabled ?? false,
		zoomLevel: state.scheduleBoard.zoomLevel,
		isAllowedToEditWorkOrders,
		isAllowedToViewProdData,
		hasOnlyNonBillableWork: !!workOrdersWithOnlyNonBillableWork[workOrder.code],
	};
};

const mapDispatchToProps = () => ({
	setCopiedWorkOrderCode: ScheduleBoardActions.setCopiedWorkOrderCode,
});

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector

);
export default enhance(OrderInfo);
